import React, { useState, useMemo, useCallback, useEffect } from "react";
import WeeklyAvailabilityGrid from "./WeeklyAvailabilityGrid";
import TimeSlotDetailModal from "./TimeSlotDetailModal";
import PrivateAppointmentModal from "./PrivateAppointmentModal";
import { ApiService } from "../../../services/api";
import { AppointmentAvailability } from "../../../types/appointment";
import { useAuth } from "../../../contexts/AuthContext";
import { useModal } from "../../../hooks/useModal";

interface TimeSlot {
  id: string;
  period: string;
  startTime: string;
  endTime: string;
}

interface AvailabilityItemUI {
  id: string;
  teacherId: string;
  day: string;
  timeSlotId: string;
  isAvailable: boolean;
}

interface SubTimeSlotAvailability {
  id: string;
  teacherId: string;
  day: string;
  timeSlotId: string;
  subTime: string;
  isAvailable: boolean;
}

const mockTimeSlots: TimeSlot[] = [
  { id: "1", period: "1. Saat", startTime: "08:00", endTime: "09:00" },
  { id: "2", period: "2. Saat", startTime: "09:00", endTime: "10:00" },
  { id: "3", period: "3. Saat", startTime: "10:00", endTime: "11:00" },
  { id: "4", period: "4. Saat", startTime: "11:00", endTime: "12:00" },
  { id: "5", period: "5. Saat", startTime: "12:00", endTime: "13:00" },
  { id: "6", period: "6. Saat", startTime: "13:00", endTime: "14:00" },
  { id: "7", period: "7. Saat", startTime: "14:00", endTime: "15:00" },
  { id: "8", period: "8. Saat", startTime: "15:00", endTime: "16:00" },
  { id: "9", period: "9. Saat", startTime: "16:00", endTime: "17:00" },
  { id: "10", period: "10. Saat", startTime: "17:00", endTime: "18:00" },
  { id: "11", period: "11. Saat", startTime: "18:00", endTime: "19:00" },
  { id: "12", period: "12. Saat", startTime: "19:00", endTime: "20:00" },
];

const days = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

const trToEnDay: Record<string, string> = {
  Pazartesi: "monday",
  Salı: "tuesday",
  Çarşamba: "wednesday",
  Perşembe: "thursday",
  Cuma: "friday",
  Cumartesi: "saturday",
  Pazar: "sunday",
};

const enToTrDay: Record<string, string> = {
  monday: "Pazartesi",
  tuesday: "Salı",
  wednesday: "Çarşamba",
  thursday: "Perşembe",
  friday: "Cuma",
  saturday: "Cumartesi",
  sunday: "Pazar",
};

const generateSubTimeSlots = (startTime: string, stepMinutes: number): string[] => {
  const [hour, minute] = startTime.split(":").map(Number);
  const subSlots: string[] = [];
  for (let i = 0; i < 60; i += stepMinutes) {
    const mins = (minute + i) % 60;
    const hrs = hour + Math.floor((minute + i) / 60);
    subSlots.push(`${hrs.toString().padStart(2, "0")}\:${mins.toString().padStart(2, "0")}`);
  }
  return subSlots;
};

const addMinutes = (hhmm: string, minsToAdd: number): string => {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(2000, 0, 1, h, m, 0, 0);
  d.setMinutes(d.getMinutes() + minsToAdd);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
};

export default function ProviderViewAvailability() {
  const { user } = useAuth();
  const { showAlert } = useModal();

  const [subTimeSlotAvailability, setSubTimeSlotAvailability] = useState<SubTimeSlotAvailability[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [privateAppointmentModalOpen, setPrivateAppointmentModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [allowedSubTimesForPrivate, setAllowedSubTimesForPrivate] = useState<string[]>([]);

  const selectedTeacherId = String(user?.id || "me");
  const [slotDuration, setSlotDuration] = useState<number>(10);
  const [defaultCapacity, setDefaultCapacity] = useState<number>(1);

  const toMinutes = (hhmm: string): number => {
    const [h, m] = String(hhmm).split(":").map(Number);
    return h * 60 + m;
  };

  const findParentTimeSlotId = (started: string): string => {
    const startedMin = toMinutes(started);
    const found = mockTimeSlots.find((ts) => {
      const s = toMinutes(ts.startTime);
      const e = toMinutes(ts.endTime);
      return startedMin >= s && startedMin < e;
    });
    return found ? String(found.id) : String(started);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await ApiService.getAppointmentAvailability();
        const uiSubs: SubTimeSlotAvailability[] = [];
        const weekly = (data as AppointmentAvailability)?.weekly || {};
        if (typeof (data as AppointmentAvailability)?.slotDuration === "number") {
          setSlotDuration((data as AppointmentAvailability).slotDuration);
        }
        if (typeof (data as AppointmentAvailability)?.defaultCapacity === "number") {
          setDefaultCapacity((data as AppointmentAvailability).defaultCapacity);
        }
        Object.entries(weekly).forEach(([engDay, ranges]: any) => {
          const trDay = enToTrDay[engDay] || engDay;
          Object.entries(ranges || {}).forEach(([range, slots]: any) => {
            (slots || []).forEach((slot: any) => {
              const timeSlotId = findParentTimeSlotId(slot.started);
              uiSubs.push({
                id: `${engDay}-${slot.started}`,
                teacherId: selectedTeacherId,
                day: trDay,
                timeSlotId: String(timeSlotId),
                subTime: slot.started,
                isAvailable: true,
              });
            });
          });
        });
        setSubTimeSlotAvailability(uiSubs);
      } catch (_) {}
    };
    load();
  }, [selectedTeacherId]);

  const checkFullAvailability = useCallback(
    (day: string, timeSlotId: string): boolean => {
      const slot = mockTimeSlots.find((s) => s.id === timeSlotId);
      if (!slot) return false;
      const expectedSubTimes = generateSubTimeSlots(slot.startTime, slotDuration || 10);
      const subSlots = subTimeSlotAvailability.filter(
        (s) => s.teacherId === selectedTeacherId && s.day === day && s.timeSlotId === timeSlotId
      );
      if (subSlots.length !== expectedSubTimes.length) return false;
      return subSlots.every((s) => s.isAvailable);
    },
    [selectedTeacherId, subTimeSlotAvailability]
  );

  const checkPartialAvailability = useCallback(
    (day: string, timeSlotId: string): boolean => {
      const slot = mockTimeSlots.find((s) => s.id === timeSlotId);
      if (!slot) return false;
      const expectedSubTimes = generateSubTimeSlots(slot.startTime, slotDuration || 10);
      const subSlots = subTimeSlotAvailability.filter(
        (s) => s.teacherId === selectedTeacherId && s.day === day && s.timeSlotId === timeSlotId
      );
      if (subSlots.length === 0) return false;
      if (subSlots.length !== expectedSubTimes.length) {
        return subSlots.some((s) => s.isAvailable);
      }
      const availableCount = subSlots.filter((s) => s.isAvailable).length;
      const totalCount = subSlots.length;
      return availableCount > 0 && availableCount < totalCount;
    },
    [selectedTeacherId, subTimeSlotAvailability]
  );

  const computedAvailability: AvailabilityItemUI[] = useMemo(() => {
    return days.flatMap((day) =>
      mockTimeSlots.map((slot) => ({
        id: `${day}-${slot.id}`,
        teacherId: selectedTeacherId,
        day,
        timeSlotId: slot.id,
        isAvailable: checkFullAvailability(day, slot.id),
      }))
    );
  }, [selectedTeacherId, subTimeSlotAvailability]);

  const handleCellClick = (day: string, timeSlotId: string) => {
    setSelectedDay(day);
    const slot = mockTimeSlots.find((s) => s.id === timeSlotId);
    setSelectedTimeSlot(slot || null);
    setModalOpen(true);
  };

  const getSubTimeSlotsForModal = () => {
    if (!selectedTimeSlot) return [];
    const subTimes = generateSubTimeSlots(selectedTimeSlot.startTime, slotDuration || 10);
    return subTimes.map((time) => {
      const existing = subTimeSlotAvailability.find(
        (s) => s.teacherId === selectedTeacherId && s.day === selectedDay && s.timeSlotId === selectedTimeSlot.id && s.subTime === time
      );
      return { time, isAvailable: existing ? existing.isAvailable : false };
    });
  };
  const reloadFromBackend = useCallback(async () => {
    try {
      const data = await ApiService.getAppointmentAvailability();
      const uiSubs: SubTimeSlotAvailability[] = [];
      const weeklyReload = (data as AppointmentAvailability)?.weekly || {};
      Object.entries(weeklyReload).forEach(([engDay, ranges]: any) => {
        const trDay = enToTrDay[engDay] || engDay;
        Object.entries(ranges || {}).forEach(([range, slots]: any) => {
          (slots || []).forEach((slot: any) => {
            const timeSlotId = findParentTimeSlotId(slot.started);
            uiSubs.push({
              id: `${engDay}-${slot.started}`,
              teacherId: selectedTeacherId,
              day: trDay,
              timeSlotId: String(timeSlotId),
              subTime: slot.started,
              isAvailable: true,
            });
          });
        });
      });
      setSubTimeSlotAvailability(uiSubs);
    } catch (_) {}
  }, [selectedTeacherId]);

  const handleModalConfirm = async (selectedTimes: string[]) => {
    if (!selectedTimeSlot) return;

    const filtered = subTimeSlotAvailability.filter(
      (s) => !(s.teacherId === selectedTeacherId && s.day === selectedDay && s.timeSlotId === (selectedTimeSlot as any).id)
    );
    const additions: SubTimeSlotAvailability[] = selectedTimes.map((time) => ({
      id: `sub-${selectedTeacherId}-${selectedDay}-${(selectedTimeSlot as any).id}-${time}`,
      teacherId: selectedTeacherId,
      day: selectedDay,
      timeSlotId: String((selectedTimeSlot as any).id),
      subTime: time,
      isAvailable: true,
    }));
    const nextSubs = [...filtered, ...additions];

    const weekly: AppointmentAvailability["weekly"] = {} as any;
    days.forEach((trDay) => {
      const enDay = trToEnDay[trDay] || trDay;
      const dayEntries: Record<string, Array<{ started: string; ended: string; capacity?: number }>> = {};
      mockTimeSlots.forEach((slot) => {
        const selectedSubs = nextSubs.filter((s) => s.day === trDay && s.timeSlotId === slot.id && s.isAvailable).map((s) => s.subTime);
        if (selectedSubs.length > 0) {
          const rangeKey = `${slot.startTime}-${slot.endTime}`;
          if (!dayEntries[rangeKey]) dayEntries[rangeKey] = [];
          selectedSubs.forEach((st) => {
            dayEntries[rangeKey].push({
              started: st,
              ended: addMinutes(st, slotDuration || 10),
            });
          });
        }
      });
      if (Object.keys(dayEntries).length > 0) {
        (weekly as any)[enDay] = dayEntries;
      }
    });

    const payload: AppointmentAvailability = {
      providerId: selectedTeacherId,
      weekly,
      defaultCapacity: defaultCapacity,
      workingHours: { start: "08:00", end: "20:00" },
      slotDuration: slotDuration || 10,
      metadata: {},
    };

    try {
      await ApiService.updateAppointmentAvailability(payload);
      await reloadFromBackend();
    } catch (_) {}
  };

  const handlePrivateAppointmentClick = async () => {
    try {
      const allowed = getSubTimeSlotsForModal()
        .filter((s) => s.isAvailable)
        .map((s) => s.time);

      if (allowed.length === 0) {
        const timeInfo = selectedTimeSlot ? `${selectedTimeSlot.period} (${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime})` : "";
        await showAlert({
          title: "Müsait Takvim Bulunamadı",
          message: `${selectedDay} günü için ${timeInfo} saatinde müsait takvim bulunamadı. Lütfen başka bir gün veya saat dilimi seçiniz.`,
          type: "warning",
        });
        return;
      }

      const dateStr = getDateForDay(selectedDay);
      const res = await ApiService.getMyProviderAppointments({
        startDate: dateStr,
        endDate: dateStr,
        status: "all",
        limit: 1000,
      });

      const booked = new Set<string>();
      (res?.data || []).forEach((apt: any) => {
        if (apt.status === "APPROVED" || apt.status === "PENDING") {
          const startTime = new Date(apt.startDate).toTimeString().slice(0, 5);
          booked.add(startTime);
        }
      });

      const filteredAllowed = allowed.filter((t) => !booked.has(t));
      if (filteredAllowed.length === 0) {
        const timeInfo = selectedTimeSlot ? `${selectedTimeSlot.period} (${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime})` : "";
        await showAlert({
          title: "Müsait Takvim Bulunamadı",
          message: `${selectedDay} günü için ${timeInfo} saatinde müsait takvim bulunamadı. Lütfen başka bir gün veya saat dilimi seçiniz.`,
          type: "warning",
        });
        return;
      }

      setAllowedSubTimesForPrivate(filteredAllowed);
      setModalOpen(false);
      setPrivateAppointmentModalOpen(true);
    } catch (_) {
      const timeInfo = selectedTimeSlot ? `${selectedTimeSlot.period} (${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime})` : "";
      await showAlert({
        title: "Müsait Takvim Bulunamadı",
        message: `${selectedDay} günü için ${timeInfo} saatinde müsait takvim bulunamadı. Lütfen daha sonra tekrar deneyin.`,
        type: "warning",
      });
    }
  };

  const handlePrivateAppointmentSuccess = async () => {
    await reloadFromBackend();
  };

  const getCurrentDate = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getDateForDay = (trDay: string): string => {
    const today = new Date();
    const currentDayIndex = today.getDay();
    const dayMap: Record<string, number> = {
      Pazar: 0,
      Pazartesi: 1,
      Salı: 2,
      Çarşamba: 3,
      Perşembe: 4,
      Cuma: 5,
      Cumartesi: 6,
    };
    const targetDayIndex = dayMap[trDay] || 0;
    const daysToAdd = (targetDayIndex - currentDayIndex + 7) % 7;
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysToAdd);
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, "0");
    const day = String(targetDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="pb-2">
      <WeeklyAvailabilityGrid
        refined
        days={days}
        timeSlots={mockTimeSlots}
        availability={computedAvailability}
        selectedEntityId={selectedTeacherId}
        getEntityId={(item) => (item as AvailabilityItemUI).teacherId}
        getIsAvailable={(item) => (item as AvailabilityItemUI).isAvailable}
        getDay={(item) => (item as AvailabilityItemUI).day}
        getTimeSlotId={(item) => (item as AvailabilityItemUI).timeSlotId}
        onToggle={handleCellClick}
        getPartialAvailability={checkPartialAvailability}
        onCellClick={handleCellClick}
        disabled={false}
        showLegend={true}
      />

      <TimeSlotDetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        day={selectedDay}
        timeSlot={selectedTimeSlot || { period: "", startTime: "", endTime: "" }}
        subTimeSlots={getSubTimeSlotsForModal()}
        disabled={false}
        readOnly={false}
        onPrivateAppointment={handlePrivateAppointmentClick}
        onConfirm={handleModalConfirm}
      />

      <PrivateAppointmentModal
        isOpen={privateAppointmentModalOpen}
        onClose={() => setPrivateAppointmentModalOpen(false)}
        day={selectedDay}
        date={getDateForDay(selectedDay)}
        timeSlot={selectedTimeSlot || { period: "", startTime: "", endTime: "" }}
        slotDuration={slotDuration}
        onSuccess={handlePrivateAppointmentSuccess}
        allowedSubTimes={allowedSubTimesForPrivate}
      />
    </div>
  );
}
